import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { withStyles } from '@material-ui/core/styles';

import ApIngestionContainer from '../../containers/apAutomation/ApIngestion';
import Transition from './Transition';

const styles = () => ({
	dialogPaper: {
		maxWidth: '95vw',
		width: '95vw',
		height: '95vh',
		maxHeight: '95vh',
		margin: '10px',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	titleBar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '8px 16px',
		borderBottom: '1px solid #ddd',
		flexShrink: 0,
	},
	body: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
	},
	leftPane: {
		flex: 1,
		overflowY: 'auto',
		padding: '0 16px 16px 16px',
	},
	rightPane: {
		width: '45%',
		flexShrink: 0,
		borderLeft: '2px solid #ddd',
		display: 'flex',
		flexDirection: 'column',
	},
	rightPaneLabel: {
		padding: '6px 12px',
		fontSize: 11,
		fontWeight: 'bold',
		color: '#888',
		background: '#f9f9f9',
		borderBottom: '1px solid #ddd',
		flexShrink: 0,
	},
	pdfEmbed: {
		flex: 1,
		width: '100%',
		border: 'none',
	},
	section: {
		borderTop: '1px solid #ddd',
		marginTop: 14,
		paddingTop: 10,
	},
	sectionHeader: {
		fontWeight: 'bold',
		fontSize: 13,
		marginBottom: 6,
		color: '#333',
	},
	jsonBlock: {
		background: '#f5f5f5',
		border: '1px solid #e0e0e0',
		borderRadius: 3,
		padding: '6px 8px',
		fontSize: 11,
		overflowX: 'auto',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
		margin: '3px 0 6px 0',
		fontFamily: 'monospace',
	},
	matchRow: {
		marginBottom: 4,
		fontSize: 12,
	},
	statusMatched: {
		color: '#2E7D32',
		fontWeight: 'bold',
	},
	statusBad: {
		color: '#C62828',
		fontWeight: 'bold',
	},
	statusNeutral: {
		color: '#555',
	},
});

class ApIngestionDebugDialog extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: false,
			debugData: null,
		};
	}

	componentDidUpdate(prevProps) {
		const { open, apIngestionId } = this.props;
		if (open && apIngestionId && (!prevProps.open || apIngestionId !== prevProps.apIngestionId)) {
			this.fetchDebugData();
		}
	}

	fetchDebugData = () => {
		const { apIngestionId, apIngestionGetDebug, showErrors } = this.props;
		this.setState({ isLoading: true, debugData: null });
		apIngestionGetDebug({
			params: { apIngestionId },
			callback: (err, data) => {
				if (err) {
					this.setState({ isLoading: false });
					showErrors(err);
					return;
				}
				this.setState({ isLoading: false, debugData: data });
			},
		});
	};

	handleClose = () => {
		this.props.onClose();
	};

	getStatusClass(status) {
		const { classes } = this.props;
		if (status === 'complete' || status === 'matched') {
			return classes.statusMatched;
		}
		if (status === 'error' || status === 'noMatch') {
			return classes.statusBad;
		}
		return classes.statusNeutral;
	}

	renderResult(result) {
		const { classes } = this.props;
		return (
			<div key={result.apIngestionResultId} className={classes.section}>
				<div className={classes.sectionHeader}>Result #{result.apIngestionResultId}</div>

				<div className={classes.matchRow}>
					<span style={{ color: '#555' }}>Match Status: </span>
					<span className={this.getStatusClass(result.matchResultStatus)}>
						{result.matchResultStatus || '—'}
					</span>
					{result.matchResultStatusMessage && (
						<div style={{ marginLeft: 16, color: '#666', fontSize: 11, marginTop: 2 }}>
							{result.matchResultStatusMessage}
						</div>
					)}
				</div>

				<div className={classes.matchRow}>
					<span style={{ color: '#555' }}>Processing Status: </span>
					<span className={this.getStatusClass(result.processingStatus)}>
						{result.processingStatus || '—'}
					</span>
					{result.processingStatusMessage && (
						<div style={{ marginLeft: 16, color: '#666', fontSize: 11, marginTop: 2 }}>
							{result.processingStatusMessage}
						</div>
					)}
				</div>

				{result.mappedToToVendorBillId && (
					<div className={classes.matchRow}>
						<span style={{ color: '#555' }}>Vendor Bill: </span>
						{result.mappedToToVendorBillId}
					</div>
				)}

				{result.matchResult && (
					<div style={{ marginTop: 6 }}>
						<div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 3 }}>matchResult:</div>
						<pre className={classes.jsonBlock}>
							{JSON.stringify(result.matchResult, null, 2)}
						</pre>
					</div>
				)}
			</div>
		);
	}

	renderLeftPane() {
		const { classes } = this.props;
		const { isLoading, debugData } = this.state;

		if (isLoading) {
			return (
				<div style={{ padding: 40, textAlign: 'center' }}>
					<CircularProgress />
					<div style={{ marginTop: 10, color: '#555' }}>Loading debug data...</div>
				</div>
			);
		}

		if (!debugData) {
			return null;
		}

		const { apIngestion, apIngestionDocuments = [], apIngestionResults = [] } = debugData;

		return (
			<>
				{apIngestion && (
					<div className={classes.section}>
						<div className={classes.sectionHeader}>Ingestion</div>
						<div className={classes.matchRow}>
							<span style={{ color: '#555' }}>Status: </span>
							<span className={this.getStatusClass(apIngestion.ingestionStatus)}>
								{apIngestion.ingestionStatus}
							</span>
							{apIngestion.ingestionStatusMessage && (
								<span style={{ marginLeft: 10, color: '#555', fontSize: 11 }}>
									{apIngestion.ingestionStatusMessage}
								</span>
							)}
						</div>
						<div className={classes.matchRow}>
							<span style={{ color: '#555' }}>Created: </span>
							{apIngestion.dateCreated}
						</div>
					</div>
				)}

				{apIngestionDocuments.map((doc) => (
					<div key={doc.apIngestionDocumentId} className={classes.section}>
						<div className={classes.sectionHeader}>
							Page {doc.pageNumber}
							{doc.groupNumber != null && (
								<span style={{ marginLeft: 10, fontWeight: 'normal', color: '#555' }}>
									Group {doc.groupNumber}
								</span>
							)}
							<span
								style={{ marginLeft: 10, fontWeight: 'normal' }}
								className={this.getStatusClass(doc.ingestionStatus)}
							>
								{doc.ingestionStatus}
							</span>
							{doc.ingestionStatusMessage && (
								<span
									style={{
										marginLeft: 8,
										fontWeight: 'normal',
										color: '#555',
										fontSize: 11,
									}}
								>
									{doc.ingestionStatusMessage}
								</span>
							)}
						</div>

						{doc.matchedApGuideSlug && (
							<div className={classes.matchRow}>
								<span style={{ color: '#555' }}>Matched Guide: </span>
								{doc.matchedApGuideSlug}
							</div>
						)}

						<div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>
							Extracted Data:
						</div>
						<pre className={classes.jsonBlock}>
							{doc.extractedData ? JSON.stringify(doc.extractedData, null, 2) : '(none)'}
						</pre>
					</div>
				))}

				{apIngestionResults.map((result) => this.renderResult(result))}
			</>
		);
	}

	renderRightPane() {
		const { classes, resourceMap } = this.props;
		const { debugData } = this.state;

		const documentResourceId = _.get(debugData, ['apIngestion', 'documentResourceId']);
		const resource = documentResourceId ? resourceMap[documentResourceId] : null;
		const pdfUrl = resource ? resource.url : null;

		return (
			<div className={classes.rightPane}>
				<div className={classes.rightPaneLabel}>ORIGINAL PDF</div>
				{pdfUrl ? (
					<object data={pdfUrl} type="application/pdf" className={classes.pdfEmbed}>
						<a href={pdfUrl}>Open PDF</a>
					</object>
				) : (
					<div
						style={{
							flex: 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#aaa',
							fontSize: 13,
						}}
					>
						{debugData ? 'PDF not available' : 'Load debug data to view PDF'}
					</div>
				)}
			</div>
		);
	}

	render() {
		const { classes, fullScreen, open, apIngestionId } = this.props;

		return (
			<Dialog
				fullScreen={fullScreen}
				maxWidth={false}
				open={open}
				TransitionComponent={Transition}
				onClose={this.handleClose}
				classes={{ paper: classes.dialogPaper }}
			>
				<div className={classes.titleBar}>
					<DialogTitle style={{ padding: 0 }}>
						Debug — AP Ingestion #{apIngestionId}
					</DialogTitle>
					<Button variant="contained" size="small" onClick={this.handleClose}>
						Close
					</Button>
				</div>

				<div className={classes.body}>
					<div className={classes.leftPane}>{this.renderLeftPane()}</div>
					{this.renderRightPane()}
				</div>
			</Dialog>
		);
	}
}

ApIngestionDebugDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	fullScreen: PropTypes.bool.isRequired,
	open: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	apIngestionId: PropTypes.number,
	resourceMap: PropTypes.object,
	showErrors: PropTypes.func,
	apIngestionGetDebug: PropTypes.func,
};

export default _.flow([withStyles(styles), withMobileDialog(), ApIngestionContainer])(
	ApIngestionDebugDialog
);
